"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { c as _c } from "react/compiler-runtime";
import { Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import React from "react";
import { cx } from "@/lib/tailwind-utils";
export interface BoxPlotEntry {
  name: string;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

// Hardcoded hex palette — CSS vars don't work inside recharts SVG
const PALETTE: string[] = ["#3b82f6",
// blue
"#10b981",
// emerald
"#8b5cf6",
// violet
"#f59e0b",
// amber
"#06b6d4",
// cyan
"#ec4899",
// pink
"#84cc16",
// lime
"#d946ef",
// fuchsia
"#6b7280" // gray
];
interface BoxShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  index?: number;
  payload?: BoxPlotEntry;
}

/** Custom SVG shape that draws whiskers, box, and median line for one box plot entry. */
const BoxShape = t0 => {
  const $ = _c(43);
  const {
    x: t1,
    y: t2,
    width: t3,
    height: t4,
    index: t5,
    payload
  } = t0;
  const x = t1 === undefined ? 0 : t1;
  const y = t2 === undefined ? 0 : t2;
  const width = t3 === undefined ? 0 : t3;
  const height = t4 === undefined ? 0 : t4;
  const index = t5 === undefined ? 0 : t5;
  if (!payload) {
    return null;
  }
  const {
    min: minVal,
    q1,
    median,
    q3,
    max: maxVal
  } = payload;
  const range = maxVal - minVal;
  if (range === 0) {
    return null;
  }
  const color = PALETTE[index % PALETTE.length];
  let t6;
  if ($[0] !== height || $[1] !== minVal || $[2] !== range || $[3] !== y) {
    t6 = v => y + height * (1 - (v - minVal) / range);
    $[0] = height;
    $[1] = minVal;
    $[2] = range;
    $[3] = y;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const toPixel = t6;
  const yMin = y + height;
  const yMax = y;
  const yQ1 = toPixel(q1);
  const yMedian = toPixel(median);
  const yQ3 = toPixel(q3);
  const cx = x + width / 2;
  const whiskerHalfWidth = width * 0.25;
  const boxX = x + width * 0.1;
  const boxWidth = width * 0.8;
  let t7;
  if ($[5] !== color || $[6] !== cx || $[7] !== yMax || $[8] !== yQ3) {
    t7 = <line x1={cx} y1={yMax} x2={cx} y2={yQ3} stroke={color} strokeWidth={1.5} />;
    $[5] = color;
    $[6] = cx;
    $[7] = yMax;
    $[8] = yQ3;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = cx - whiskerHalfWidth;
  const t9 = cx + whiskerHalfWidth;
  let t10;
  if ($[10] !== color || $[11] !== t8 || $[12] !== t9 || $[13] !== yMax) {
    t10 = <line x1={t8} y1={yMax} x2={t9} y2={yMax} stroke={color} strokeWidth={1.5} />;
    $[10] = color;
    $[11] = t8;
    $[12] = t9;
    $[13] = yMax;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = yQ1 - yQ3;
  let t12;
  if ($[15] !== boxWidth || $[16] !== boxX || $[17] !== color || $[18] !== t11 || $[19] !== yQ3) {
    t12 = <rect x={boxX} y={yQ3} width={boxWidth} height={t11} fill={color} fillOpacity={0.3} stroke={color} strokeWidth={1.5} />;
    $[15] = boxWidth;
    $[16] = boxX;
    $[17] = color;
    $[18] = t11;
    $[19] = yQ3;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const t13 = boxX + boxWidth;
  let t14;
  if ($[21] !== boxX || $[22] !== color || $[23] !== t13 || $[24] !== yMedian) {
    t14 = <line x1={boxX} y1={yMedian} x2={t13} y2={yMedian} stroke={color} strokeWidth={2.5} />;
    $[21] = boxX;
    $[22] = color;
    $[23] = t13;
    $[24] = yMedian;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== color || $[27] !== cx || $[28] !== yMin || $[29] !== yQ1) {
    t15 = <line x1={cx} y1={yQ1} x2={cx} y2={yMin} stroke={color} strokeWidth={1.5} />;
    $[26] = color;
    $[27] = cx;
    $[28] = yMin;
    $[29] = yQ1;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const t16 = cx - whiskerHalfWidth;
  const t17 = cx + whiskerHalfWidth;
  let t18;
  if ($[31] !== color || $[32] !== t16 || $[33] !== t17 || $[34] !== yMin) {
    t18 = <line x1={t16} y1={yMin} x2={t17} y2={yMin} stroke={color} strokeWidth={1.5} />;
    $[31] = color;
    $[32] = t16;
    $[33] = t17;
    $[34] = yMin;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t10 || $[37] !== t12 || $[38] !== t14 || $[39] !== t15 || $[40] !== t18 || $[41] !== t7) {
    t19 = <g>{t7}{t10}{t12}{t14}{t15}{t18}</g>;
    $[36] = t10;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
    $[40] = t18;
    $[41] = t7;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  return t19;
};
interface BoxPlotTooltipProps {
  active?: boolean;
  payload?: any[];
  valueFormatter?: (v: number) => string;
}
const BoxPlotTooltip = ({
  active,
  payload,
  valueFormatter = v => v.toLocaleString()
}: BoxPlotTooltipProps) => {
  if (!active || !payload?.length) return null;
  const d: BoxPlotEntry = payload[0]?.payload;
  if (!d) return null;
  return <div className="rounded-md border border-border bg-background/90 px-3 py-2 text-body shadow-md backdrop-blur-2xl">
      <p className="mb-1 font-medium text-foreground">{d.name}</p>
      <div className="space-y-0.5 text-foreground-secondary">
        <p>
          Max:{" "}
          <span className="tabular-nums text-foreground">
            {valueFormatter(d.max)}
          </span>
        </p>
        <p>
          Q3:{" "}
          <span className="tabular-nums text-foreground">
            {valueFormatter(d.q3)}
          </span>
        </p>
        <p>
          Median:{" "}
          <span className="tabular-nums font-medium text-foreground">
            {valueFormatter(d.median)}
          </span>
        </p>
        <p>
          Q1:{" "}
          <span className="tabular-nums text-foreground">
            {valueFormatter(d.q1)}
          </span>
        </p>
        <p>
          Min:{" "}
          <span className="tabular-nums text-foreground">
            {valueFormatter(d.min)}
          </span>
        </p>
      </div>
    </div>;
};
export interface BoxPlotChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: BoxPlotEntry[];
  valueFormatter?: (value: number) => string;
}
const BoxPlotChart = React.forwardRef<HTMLDivElement, BoxPlotChartProps>((t0, forwardedRef) => {
  const $ = _c(30);
  let className;
  let data;
  let other;
  let valueFormatter;
  if ($[0] !== t0) {
    ({
      data,
      valueFormatter,
      className,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = data;
    $[3] = other;
    $[4] = valueFormatter;
  } else {
    className = $[1];
    data = $[2];
    other = $[3];
    valueFormatter = $[4];
  }
  let t1;
  if ($[5] !== data) {
    t1 = data.map(_temp);
    $[5] = data;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const chartData = t1;
  let t2;
  if ($[7] !== className) {
    t2 = cx("h-80 w-full", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      top: 8,
      right: 16,
      bottom: 8,
      left: 8
    };
    t4 = <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <XAxis dataKey="name" tick={{
      fontSize: 12,
      className: "fill-foreground-secondary text-caption"
    }} axisLine={false} tickLine={false} />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      fontSize: 12,
      className: "fill-foreground-secondary text-caption"
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== valueFormatter) {
    t7 = <YAxis tickFormatter={valueFormatter} tick={t6} axisLine={false} tickLine={false} width={60} />;
    $[13] = valueFormatter;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== valueFormatter) {
    t8 = t9 => {
      const {
        active,
        payload
      } = t9;
      return <BoxPlotTooltip active={active} payload={payload ? [...payload] : []} valueFormatter={valueFormatter} />;
    };
    $[15] = valueFormatter;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      fill: "transparent"
    };
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8) {
    t10 = <Tooltip content={t8} cursor={t9} />;
    $[18] = t8;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Bar dataKey="range" shape={_temp2} isAnimationActive={false} />;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== chartData || $[22] !== t10 || $[23] !== t7) {
    t12 = <ResponsiveContainer width="100%" height="100%"><ComposedChart data={chartData} margin={t3}>{t4}{t5}{t7}{t10}{t11}</ComposedChart></ResponsiveContainer>;
    $[21] = chartData;
    $[22] = t10;
    $[23] = t7;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== forwardedRef || $[26] !== other || $[27] !== t12 || $[28] !== t2) {
    t13 = <div ref={forwardedRef} className={t2} tremor-id="tremor-raw" {...other}>{t12}</div>;
    $[25] = forwardedRef;
    $[26] = other;
    $[27] = t12;
    $[28] = t2;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
});
BoxPlotChart.displayName = "BoxPlotChart";
export { BoxPlotChart };
function _temp(d) {
  return {
    ...d,
    range: [d.min, d.max] as [number, number]
  };
}
function _temp2(props) {
  return <BoxShape {...props} />;
}